// ============================================================
// PhotoEditorPanel.jsx — top-level Photo Editor workspace
// ------------------------------------------------------------
//   No photo            → PhotoBrowser (single + multi/batch select)
//   Photo selected      → EditorCanvas + rail (Filters | Edit | Text)
//                         + Save / Download / Cancel
//   Multiple selected   → batch mode (same filter/adjustments to all; Text
//                         tab disabled; uniform new/overwrite choice)
//
// Persistence is client-side via the anon Supabase client under RLS
// (usePhotoEditor). Styling is scoped to `.pe-*` and reuses the global
// dashboard design tokens.
// ============================================================

import React, { useState } from 'react';
import { usePhotoEditor } from '../hooks/usePhotoEditor';
import PhotoBrowser from '../components/photoeditor/PhotoBrowser';
import EditorCanvas from '../components/photoeditor/EditorCanvas';
import FilterStrip from '../components/photoeditor/FilterStrip';
import AdjustmentPanel from '../components/photoeditor/AdjustmentPanel';
import TextOverlayTool from '../components/photoeditor/TextOverlayTool';
import CropTool from '../components/photoeditor/CropTool';
import SaveModal from '../components/photoeditor/SaveModal';
import CustomFilterModal from '../components/photoeditor/CustomFilterModal';

const TABS = [
  { id: 'crop',    label: 'Crop' },
  { id: 'filters', label: 'Filters' },
  { id: 'edit',    label: 'Edit' },
  { id: 'text',    label: 'Text' },
];

// Tabs that only make sense on a single photo — hidden in batch mode.
const PER_PHOTO_TABS = ['crop', 'text'];

export default function PhotoEditorPanel({ clientId }) {
  const [toast, setToast] = useState({ visible: false, msg: '' });
  const [tab, setTab] = useState('filters');
  const [saveOpen, setSaveOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [batchPhotos, setBatchPhotos] = useState([]); // [] = single mode
  const [batchSaving, setBatchSaving] = useState(false);

  const showToast = (msg) => {
    setToast({ visible: true, msg });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2600);
  };

  const ed = usePhotoEditor({ clientId, showToast });
  const isBatch = batchPhotos.length > 1;
  const busy = ed.isSaving || batchSaving;

  const resetView = () => {
    ed.clearPhoto();
    setBatchPhotos([]);
    setTab('filters');
  };

  const handleCancel = () => {
    if (ed.isDirty && !window.confirm('You have unsaved edits — leave without saving?')) return;
    resetView();
  };

  const handlePickMultiple = (photos) => {
    setBatchPhotos(photos);
    ed.loadPhoto(photos[0]);
    if (PER_PHOTO_TABS.includes(tab)) setTab('filters');
  };

  const handleSaveChoice = async (mode) => {
    if (isBatch) {
      setBatchSaving(true);
      try {
        for (let i = 0; i < batchPhotos.length; i++) {
          showToast(`Saving ${i + 1} of ${batchPhotos.length}…`);
          await ed.savePhotoWithSettings(batchPhotos[i], ed.adjustments, mode);
        }
        showToast(`Saved ${batchPhotos.length} photos ✓`);
        setSaveOpen(false);
        setRefreshKey((k) => k + 1);
        resetView();
      } catch (err) {
        console.error('[PhotoEditor] batch save failed:', err);
        showToast(`Batch save failed: ${err.message}`);
      } finally {
        setBatchSaving(false);
      }
      return;
    }
    const saved = await ed.saveToLibrary(mode);
    if (saved) {
      setSaveOpen(false);
      setRefreshKey((k) => k + 1);
      resetView();
    }
  };

  const handleSaveCustom = async (name, thumb) => {
    await ed.saveCustomFilter(name, thumb);
    setCustomOpen(false);
  };

  const activeTabs = isBatch ? TABS.filter((t) => !PER_PHOTO_TABS.includes(t.id)) : TABS;

  return (
    <div className="pe-root">
      <style>{PE_STYLES}</style>

      {!ed.selectedPhoto ? (
        <PhotoBrowser
          clientId={clientId}
          onPick={ed.loadPhoto}
          onPickMultiple={handlePickMultiple}
          refreshKey={refreshKey}
        />
      ) : (
        <div className="pe-editor">
          <div className="pe-stage-col">
            {isBatch && (
              <div className="pe-batch-banner">
                Editing {batchPhotos.length} photos — the same filter &amp; adjustments apply to all.
              </div>
            )}
            <EditorCanvas
              photo={ed.selectedPhoto}
              settings={ed.adjustments}
              textLayers={isBatch ? [] : ed.textLayers}
              activeTextLayerId={ed.activeTextLayerId}
              interactive={!isBatch}
              onSelectLayer={ed.selectTextLayer}
              onMoveLayer={ed.updateTextLayer}
              cropMode={tab === 'crop' && !isBatch}
              crop={ed.crop}
              cropAspect={ed.cropAspect}
              onCropChange={ed.setCrop}
            />
          </div>

          <div className="pe-rail">
            <div className="pe-tabs">
              {activeTabs.map((t) => (
                <button
                  key={t.id}
                  className={`pe-tab${tab === t.id ? ' active' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="pe-tab-body">
              {tab === 'crop' && !isBatch && (
                <CropTool
                  aspect={ed.cropAspect}
                  onAspect={ed.setCropAspect}
                  onReset={ed.resetCrop}
                />
              )}
              {tab === 'filters' && (
                <FilterStrip
                  photo={ed.selectedPhoto}
                  activeFilter={ed.activeFilter}
                  filterStrength={ed.filterStrength}
                  onSelect={ed.applyPreset}
                  onStrength={ed.setStrength}
                  customFilters={ed.customFilters}
                  favouriteFilters={ed.favouriteFilters}
                  onSelectCustom={ed.applyCustomFilter}
                  onToggleFav={ed.toggleFavourite}
                  onDeleteCustom={ed.deleteCustomFilter}
                  onSaveCurrent={() => setCustomOpen(true)}
                />
              )}
              {tab === 'edit' && (
                <AdjustmentPanel
                  adjustments={ed.adjustments}
                  onChange={ed.updateAdjustment}
                  onReset={ed.resetAdjustment}
                  onResetAll={ed.resetAdjustments}
                  onSaveCustom={() => setCustomOpen(true)}
                />
              )}
              {tab === 'text' && !isBatch && (
                <TextOverlayTool
                  layers={ed.textLayers}
                  activeId={ed.activeTextLayerId}
                  onAdd={ed.addTextLayer}
                  onSelect={ed.selectTextLayer}
                  onUpdate={ed.updateTextLayer}
                  onRemove={ed.removeTextLayer}
                />
              )}
            </div>

            <div className="pe-actions">
              <button
                className="pe-btn pe-btn-primary pe-btn-block"
                disabled={busy}
                onClick={() => setSaveOpen(true)}
              >
                {busy ? 'Working…' : isBatch ? `Save ${batchPhotos.length} to Library` : 'Save to Library'}
              </button>
              <div className="pe-actions-row">
                {!isBatch && (
                  <button className="pe-btn pe-btn-ghost" disabled={busy} onClick={ed.downloadPhoto}>
                    Download
                  </button>
                )}
                <button className="pe-btn pe-btn-ghost" disabled={busy} onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SaveModal
        open={saveOpen}
        busy={busy}
        batchCount={batchPhotos.length}
        onChoose={handleSaveChoice}
        onClose={() => setSaveOpen(false)}
      />
      <CustomFilterModal
        open={customOpen}
        photoUrl={ed.selectedPhoto?.url}
        settings={ed.adjustments}
        onSave={handleSaveCustom}
        onClose={() => setCustomOpen(false)}
      />

      <div className={`pe-toast${toast.visible ? ' show' : ''}`}>{toast.msg}</div>
    </div>
  );
}

// ── Scoped styles ───────────────────────────────────────────────────────
const PE_STYLES = `
.pe-root { height:100%; color:var(--text-primary); }
.pe-empty, .pe-soon { display:flex; align-items:center; justify-content:center; text-align:center;
  min-height:240px; color:var(--text-secondary); font-size:13px; padding:24px; }

/* Browser */
.pe-browser-toolbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; gap:12px; flex-wrap:wrap; }
.pe-browser-title { font-size:15px; font-weight:600; }
.pe-browser-actions { display:flex; gap:8px; }
.pe-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:12px; }
.pe-tile { position:relative; aspect-ratio:1; border-radius:10px; overflow:hidden; cursor:pointer;
  border:2px solid transparent; background:var(--bg-surface); box-shadow:0 2px 8px rgba(0,0,0,0.25); }
.pe-tile img { width:100%; height:100%; object-fit:cover; display:block; }
.pe-tile.selected { border-color:var(--accent); }
.pe-tile-hover { position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  background:rgba(0,0,0,0.35); opacity:0; transition:opacity .15s; }
.pe-tile:hover .pe-tile-hover { opacity:1; }
.pe-tile-badge { position:absolute; top:6px; left:6px; font-size:9px; font-weight:700; text-transform:uppercase;
  letter-spacing:.05em; color:var(--text-secondary); background:rgba(0,0,0,0.55); padding:2px 6px; border-radius:6px; }
.pe-tile-check { position:absolute; top:6px; right:6px; width:20px; height:20px; border-radius:50%;
  border:2px solid #fff; background:rgba(0,0,0,0.35); display:flex; align-items:center; justify-content:center;
  font-size:11px; font-weight:800; color:#fff; }
.pe-tile-check.on { background:var(--accent); border-color:var(--accent); color:var(--bg-base); }

/* Editor layout */
.pe-editor { display:flex; gap:16px; height:100%; align-items:stretch; }
.pe-stage-col { flex:1; min-width:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; }
.pe-batch-banner { width:100%; text-align:center; font-size:12px; color:var(--accent);
  background:var(--accent-dim); border:1px solid var(--accent-border); border-radius:9px; padding:8px 12px; }
.pe-canvas-wrap { width:100%; flex:1; display:flex; align-items:center; justify-content:center; min-height:0; }
.pe-canvas-stage { position:relative; max-width:100%; max-height:72vh; line-height:0;
  border-radius:12px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.4); }
.pe-canvas-img { max-width:100%; max-height:72vh; display:block; object-fit:contain; }
.pe-canvas-overlay { position:absolute; inset:0; pointer-events:none; }

/* Tilt-shift bands + grain noise overlay */
.pe-tilt-band { position:absolute; left:0; right:0; pointer-events:none; }
.pe-grain-canvas { position:absolute; inset:0; width:100%; height:100%; pointer-events:none;
  mix-blend-mode:overlay; }

/* Text layers (preview) */
.pe-textlayer { position:absolute; transform:translate(-50%,-50%); white-space:pre-wrap;
  line-height:1.2; box-sizing:border-box; user-select:none; touch-action:none; word-break:break-word; }
.pe-textlayer.active { outline:1.5px dashed var(--accent); outline-offset:3px; }

/* Crop overlay */
.pe-crop-overlay { position:absolute; inset:0; z-index:5; touch-action:none; }
.pe-crop-dim { position:absolute; background:rgba(0,0,0,0.55); pointer-events:none; }
.pe-crop-box { position:absolute; box-sizing:border-box; cursor:move;
  border:1px solid rgba(255,255,255,0.9); box-shadow:0 0 0 1px rgba(0,0,0,0.4); touch-action:none; }
.pe-crop-grid { position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px);
  background-size:33.333% 33.333%; background-position:0 0; }
.pe-crop-h { position:absolute; width:14px; height:14px; box-sizing:border-box;
  background:var(--accent); border:2px solid #fff; border-radius:50%; touch-action:none; }
.pe-crop-h-nw { left:-7px;  top:-7px;    cursor:nwse-resize; }
.pe-crop-h-ne { right:-7px; top:-7px;    cursor:nesw-resize; }
.pe-crop-h-sw { left:-7px;  bottom:-7px; cursor:nesw-resize; }
.pe-crop-h-se { right:-7px; bottom:-7px; cursor:nwse-resize; }
.pe-crop-h-n  { left:50%;   top:-7px;    transform:translateX(-50%); cursor:ns-resize; }
.pe-crop-h-s  { left:50%;   bottom:-7px; transform:translateX(-50%); cursor:ns-resize; }
.pe-crop-h-e  { right:-7px; top:50%;     transform:translateY(-50%); cursor:ew-resize; }
.pe-crop-h-w  { left:-7px;  top:50%;     transform:translateY(-50%); cursor:ew-resize; }
.pe-crop-hint { font-size:11px; color:var(--text-muted); line-height:1.5; margin:14px 0; }

/* Right rail (glass card) */
.pe-rail { width:320px; min-width:320px; display:flex; flex-direction:column;
  background:rgba(20,23,32,0.7); backdrop-filter:blur(12px); border:1px solid var(--border);
  border-radius:14px; overflow:hidden; }
.pe-tabs { display:flex; padding:8px; gap:6px; border-bottom:1px solid var(--border); }
.pe-tab { flex:1; padding:8px; border-radius:8px; border:none; cursor:pointer; font-size:12px; font-weight:600;
  background:transparent; color:var(--text-secondary); transition:background .15s, color .15s; }
.pe-tab.active { background:var(--accent-dim); color:var(--accent); }
.pe-tab-body { flex:1; overflow-y:auto; padding:12px; }

/* Filter strip */
.pe-strip-head { font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase;
  color:var(--text-muted); margin:10px 0 7px; }
.pe-strip-head:first-child { margin-top:0; }
.pe-strip-head-row { display:flex; align-items:center; justify-content:space-between; }
.pe-link-btn { background:none; border:none; color:var(--accent); font-size:11px; font-weight:600; cursor:pointer; padding:0; }
.pe-strip-empty { font-size:11px; color:var(--text-muted); padding:2px 0 6px; }
.pe-strip-scroll { display:flex; gap:10px; overflow-x:auto; padding-bottom:8px;
  scrollbar-width:thin; scrollbar-color:var(--accent-border) transparent; scroll-snap-type:x proximity; }
.pe-strip-scroll::-webkit-scrollbar { height:6px; }
.pe-strip-scroll::-webkit-scrollbar-thumb { background:var(--accent-border); border-radius:3px; }
.pe-strip-scroll::-webkit-scrollbar-thumb:hover { background:var(--accent); }
.pe-strip-scroll::-webkit-scrollbar-track { background:transparent; }
.pe-strip-scroll .pe-filter-item { scroll-snap-align:start; }
/* Presets + My Filters: 4-column wrapping grid */
.pe-strip-grid { display:grid; grid-template-columns:repeat(4, 1fr); gap:10px 8px; padding-bottom:4px; }
.pe-strip-grid .pe-filter-item { width:auto; }
.pe-strip-grid .pe-filter-btn { width:100%; }
.pe-strip-grid .pe-filter-thumb { width:100%; height:auto; aspect-ratio:1; }
.pe-strip-grid .pe-filter-name { max-width:100%; }
.pe-filter-item { position:relative; flex:0 0 auto; width:64px; }
.pe-filter-btn { background:none; border:none; cursor:pointer; padding:0; width:64px;
  display:flex; flex-direction:column; align-items:center; gap:5px; }
.pe-filter-thumb { width:60px; height:60px; border-radius:8px; overflow:hidden; border:2px solid transparent;
  background:var(--bg-base); box-shadow:0 2px 6px rgba(0,0,0,0.3); }
.pe-filter-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.pe-filter-thumb-ph { display:block; width:100%; height:100%; background:linear-gradient(135deg, #1d2230, #11141c); }
.pe-filter-item.active .pe-filter-thumb { border-color:var(--accent); }
.pe-filter-name { font-size:10px; color:var(--text-secondary); white-space:nowrap; max-width:64px; overflow:hidden; text-overflow:ellipsis; }
.pe-filter-item.active .pe-filter-name { color:var(--accent); }
.pe-star { position:absolute; top:-4px; right:-2px; width:18px; height:18px; border:none; border-radius:50%;
  background:rgba(0,0,0,0.55); color:rgba(255,255,255,0.5); font-size:11px; line-height:1; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }
.pe-star.on { color:var(--accent); }
.pe-trash { position:absolute; top:-4px; left:-2px; width:18px; height:18px; border:none; border-radius:50%;
  background:rgba(0,0,0,0.55); font-size:9px; line-height:1; cursor:pointer; }
.pe-strength { display:flex; align-items:center; gap:8px; margin-top:14px; }
.pe-strength-label { font-size:11px; color:var(--text-secondary); white-space:nowrap; }
.pe-strength-val { font-size:11px; color:var(--accent); width:34px; text-align:right; }

/* Adjustment sliders */
.pe-adj-row { margin-bottom:14px; }
.pe-adj-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:5px; }
.pe-adj-label { display:flex; align-items:center; gap:7px; font-size:12px; color:var(--text-primary); }
.pe-adj-val { font-size:11px; color:var(--text-muted); cursor:pointer; font-variant-numeric:tabular-nums; }
.pe-adj-val.changed { color:var(--accent); font-weight:600; }
.pe-reset-all { width:100%; margin-top:4px; }

/* Text tool */
.pe-text-input { width:100%; box-sizing:border-box; background:var(--bg-base); border:1px solid var(--border);
  border-radius:8px; color:var(--text-primary); font-size:12px; padding:8px 10px; resize:vertical; }
.pe-text-input:focus { outline:none; border-color:var(--accent-border); }
.pe-field-label { font-size:10px; font-weight:700; letter-spacing:.05em; text-transform:uppercase;
  color:var(--text-muted); margin:14px 0 6px; }
.pe-layer-list { display:flex; flex-direction:column; gap:6px; margin-top:10px; }
.pe-layer-row { display:flex; align-items:center; justify-content:space-between; gap:8px; cursor:pointer;
  background:var(--bg-base); border:1px solid var(--border); border-radius:8px; padding:7px 10px; }
.pe-layer-row.active { border-color:var(--accent-border); background:var(--accent-dim); }
.pe-layer-text { font-size:12px; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pe-layer-del { background:none; border:none; color:var(--text-secondary); font-size:16px; cursor:pointer; line-height:1; }
.pe-text-editor { margin-top:14px; }
.pe-style-grid { display:flex; flex-wrap:wrap; gap:6px; }
.pe-style-chip { background:var(--bg-base); border:1px solid var(--border); color:var(--text-secondary);
  font-size:11px; padding:6px 10px; border-radius:7px; cursor:pointer; }
.pe-style-chip.active { border-color:var(--accent); color:var(--accent); background:var(--accent-dim); }
.pe-swatches { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.pe-swatch { width:22px; height:22px; border-radius:50%; border:2px solid var(--border); cursor:pointer; padding:0; }
.pe-swatch.active { border-color:var(--accent); transform:scale(1.1); }
.pe-swatch-custom { background:conic-gradient(red, yellow, lime, cyan, blue, magenta, red); }
.pe-align-row { display:flex; gap:6px; }
.pe-align-btn { flex:1; background:var(--bg-base); border:1px solid var(--border); color:var(--text-secondary);
  font-size:13px; padding:6px; border-radius:7px; cursor:pointer; }
.pe-align-btn.active { border-color:var(--accent); color:var(--accent); background:var(--accent-dim); }

/* Range inputs */
.pe-range { -webkit-appearance:none; appearance:none; width:100%; height:4px; border-radius:3px;
  background:rgba(255,255,255,0.12); outline:none; cursor:pointer; margin:6px 0; }
.pe-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:14px; height:14px;
  border-radius:50%; background:var(--accent); border:2px solid var(--bg-base); box-shadow:0 0 0 1px var(--accent); }
.pe-range::-moz-range-thumb { width:14px; height:14px; border-radius:50%; background:var(--accent); border:2px solid var(--bg-base); }

/* Actions */
.pe-actions { padding:12px; border-top:1px solid var(--border); display:flex; flex-direction:column; gap:8px; }
.pe-actions-row { display:flex; gap:8px; }
.pe-actions-row .pe-btn { flex:1; }

/* Buttons */
.pe-btn { padding:9px 14px; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer;
  border:1px solid transparent; transition:background .15s, color .15s, border-color .15s; }
.pe-btn:disabled { opacity:.55; cursor:not-allowed; }
.pe-btn-sm { padding:6px 12px; font-size:11px; }
.pe-btn-block { width:100%; margin-top:4px; }
.pe-btn-primary { background:var(--accent); color:var(--bg-base); }
.pe-btn-primary:hover:not(:disabled) { background:#28c4a8; }
.pe-btn-ghost { background:transparent; color:var(--text-secondary); border-color:var(--border); }
.pe-btn-ghost:hover:not(:disabled) { color:var(--text-primary); border-color:var(--accent-border); }
.pe-btn-ghost.active { color:var(--accent); border-color:var(--accent-border); background:var(--accent-dim); }
.pe-btn-accent { background:transparent; color:var(--accent); border-color:var(--accent-border); }
.pe-btn-accent:hover:not(:disabled) { background:var(--accent-dim); }

/* Modals */
.pe-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.6); display:flex; align-items:center;
  justify-content:center; z-index:1000; backdrop-filter:blur(2px); }
.pe-modal { width:min(440px, 92vw); background:rgba(20,23,32,0.92); backdrop-filter:blur(16px);
  border:1px solid var(--border); border-radius:16px; padding:22px; box-shadow:0 20px 60px rgba(0,0,0,0.5); }
.pe-modal-title { font-size:16px; font-weight:700; margin-bottom:4px; }
.pe-modal-sub { font-size:12px; color:var(--text-secondary); margin-bottom:16px; }
.pe-modal-cards { display:flex; gap:12px; }
.pe-savecard { flex:1; display:flex; flex-direction:column; align-items:center; gap:6px; text-align:center;
  padding:18px 12px; border-radius:12px; cursor:pointer; background:var(--bg-surface);
  border:1px solid var(--border); color:var(--text-primary); transition:border-color .15s, background .15s; }
.pe-savecard:hover:not(:disabled) { border-color:var(--accent); background:var(--accent-dim); }
.pe-savecard:disabled { opacity:.55; cursor:not-allowed; }
.pe-savecard-icon { font-size:22px; color:var(--accent); line-height:1; }
.pe-savecard-title { font-size:13px; font-weight:600; }
.pe-savecard-desc { font-size:11px; color:var(--text-secondary); }
.pe-modal-foot { display:flex; align-items:center; justify-content:flex-end; gap:12px; margin-top:18px; }
.pe-modal-busy { font-size:12px; color:var(--accent); }
.pe-cf-row { display:flex; gap:14px; align-items:center; }
.pe-cf-preview { width:60px; height:60px; border-radius:10px; overflow:hidden; flex:0 0 auto;
  border:1px solid var(--border); background:var(--bg-base); }
.pe-cf-preview img { width:100%; height:100%; object-fit:cover; display:block; }
.pe-cf-name { flex:1; }

/* Toast */
.pe-toast { position:fixed; bottom:24px; right:24px; z-index:1100; max-width:320px;
  background:rgba(20,23,32,0.95); border:1px solid var(--accent-border); border-left:3px solid var(--accent);
  color:var(--text-primary); font-size:12px; padding:11px 16px; border-radius:10px; box-shadow:0 8px 28px rgba(0,0,0,0.45);
  opacity:0; transform:translateY(10px); pointer-events:none; transition:opacity .2s, transform .2s; }
.pe-toast.show { opacity:1; transform:translateY(0); }

@media (max-width: 820px) {
  .pe-editor { flex-direction:column; }
  .pe-rail { width:100%; min-width:0; }
  .pe-canvas-stage, .pe-canvas-img { max-height:46vh; }
}
`;
